/**
 * EditorialBriefService
 *
 * Source of truth: docs/03-technical/SPEC-EDITORIALBRIEF-PROOF.md
 * Issue: #170 (Sprint 8 / Phase 2A.1 alpha proof)
 *
 * Owns CRUD + status transitions + consumption helpers for the
 * EditorialBrief model (schema already deployed at
 * amplify/data/resource.ts:1111).
 *
 * Does NOT generate brief content (the PLAN-phase Lambda is deferred to
 * Sprint 9 / Phase 2A.2). For alpha, briefs are hand-seeded by the team
 * via the Amplify console for the invited advisor cohort.
 *
 * Mirrors PublicationSeriesService's class+singleton+optional-injected-
 * client pattern (CLAUDE.md Rule 20).
 */

import { generateClient } from 'aws-amplify/data';
import logger from '../utils/logger.js';
import { createValidationError } from '../utils/StandardErrorFormat.js';

const MAX_THESIS_CHARS = 2000;

const VALID_TRANSITIONS = {
  approve: ['draft'],
  reject: ['draft'],
  // #313 — sendBack moves an approved brief back to draft so the editor
  // can revise. Different from rejectBrief which is a draft→rejected
  // terminal transition.
  sendBack: ['approved'],
};

/**
 * Pure helper: normalize sectionPlans to an object regardless of whether
 * AppSync returned the AWSJSON scalar as a string (older clients) or as
 * a parsed object (newer ones). Returns {} on any parse failure so
 * callers never need to handle malformed-JSON cases.
 */
function parseSectionPlans(sectionPlans) {
  if (!sectionPlans) return {};
  if (typeof sectionPlans === 'object') return sectionPlans;
  if (typeof sectionPlans === 'string') {
    try {
      const parsed = JSON.parse(sectionPlans);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}

class EditorialBriefService {
  constructor(client = null) {
    this.logger = logger.child('EditorialBriefService');
    this.client = client || generateClient();
  }

  // ────────────────────────────────────────────────────────────────────
  // CRUD
  // ────────────────────────────────────────────────────────────────────

  async createBrief({ seriesId, sectionPlans, campaignId = null } = {}) {
    if (!seriesId || typeof seriesId !== 'string') {
      createValidationError('seriesId is required', { code: 'SERIES_ID_REQUIRED' });
    }
    if (sectionPlans === undefined || sectionPlans === null) {
      createValidationError('sectionPlans is required', { code: 'SECTION_PLANS_REQUIRED' });
    }
    if (typeof sectionPlans !== 'object' || Array.isArray(sectionPlans)) {
      createValidationError('sectionPlans must be an object', {
        code: 'SECTION_PLANS_INVALID',
      });
    }

    const cotwThesis = sectionPlans?.chart_of_the_week?.thesis;
    if (typeof cotwThesis === 'string' && cotwThesis.length > MAX_THESIS_CHARS) {
      createValidationError(
        `chart_of_the_week.thesis exceeds ${MAX_THESIS_CHARS} chars`,
        { code: 'THESIS_TOO_LONG' }
      );
    }

    const input = {
      seriesId,
      sectionPlans,
      campaignId,
      briefStatus: 'draft',
    };

    const { data } = await this.client.models.EditorialBrief.create(input);
    return data;
  }

  async getBrief(id) {
    const { data } = await this.client.models.EditorialBrief.get({ id });
    return data ?? null;
  }

  async listBriefsBySeries(seriesId) {
    const { data } = await this.client.models.EditorialBrief.list({
      filter: { seriesId: { eq: seriesId } },
    });
    return data ?? [];
  }

  async deleteBrief(id) {
    await this.client.models.EditorialBrief.delete({ id });
  }

  // ────────────────────────────────────────────────────────────────────
  // Status transitions
  // ────────────────────────────────────────────────────────────────────

  async approveBrief(id, approvedByUserId) {
    const brief = await this.getBrief(id);
    if (!brief) {
      createValidationError(`Brief not found: ${id}`, { code: 'BRIEF_NOT_FOUND' });
    }
    if (!VALID_TRANSITIONS.approve.includes(brief.briefStatus)) {
      createValidationError(
        `Cannot approve brief in status "${brief.briefStatus}" — expected one of: ${VALID_TRANSITIONS.approve.join(', ')}`,
        { code: 'INVALID_TRANSITION' }
      );
    }

    const { data } = await this.client.models.EditorialBrief.update({
      id,
      briefStatus: 'approved',
      approvedAt: new Date().toISOString(),
      approvedByUserId,
    });
    return data;
  }

  async rejectBrief(id) {
    const brief = await this.getBrief(id);
    if (!brief) {
      createValidationError(`Brief not found: ${id}`, { code: 'BRIEF_NOT_FOUND' });
    }
    if (!VALID_TRANSITIONS.reject.includes(brief.briefStatus)) {
      createValidationError(
        `Cannot reject brief in status "${brief.briefStatus}" — expected one of: ${VALID_TRANSITIONS.reject.join(', ')}`,
        { code: 'INVALID_TRANSITION' }
      );
    }

    const { data } = await this.client.models.EditorialBrief.update({
      id,
      briefStatus: 'rejected',
    });
    return data;
  }

  /**
   * #313 — Flip an approved brief back to draft so the editor can revise.
   * Different intent from rejectBrief (which is a draft→rejected terminal
   * transition). Used by the wizard's "Send back to editor" action.
   */
  async sendBackBrief(id) {
    const brief = await this.getBrief(id);
    if (!brief) {
      createValidationError(`Brief not found: ${id}`, { code: 'BRIEF_NOT_FOUND' });
    }
    if (!VALID_TRANSITIONS.sendBack.includes(brief.briefStatus)) {
      createValidationError(
        `Cannot send back brief in status "${brief.briefStatus}" — expected one of: ${VALID_TRANSITIONS.sendBack.join(', ')}`,
        { code: 'INVALID_TRANSITION' }
      );
    }

    const { data } = await this.client.models.EditorialBrief.update({
      id,
      briefStatus: 'draft',
    });
    return data;
  }

  // ────────────────────────────────────────────────────────────────────
  // Per-section editing (#313 — powers inline edits in BriefApprovalStep)
  // ────────────────────────────────────────────────────────────────────

  /**
   * #313 — Edit a single section's thesis in place. Reads the current
   * sectionPlans (parsing the AWSJSON string if needed), patches the one
   * key, and writes the whole object back. Leaves briefStatus alone so
   * approved-after-edit stays approved (editor + advisor are aligned —
   * only "Send back" reverts status).
   *
   * @param {string} id - EditorialBrief id
   * @param {string} sectionKey - one of BRIEF_AWARE_SECTIONS (regeneration.js)
   * @param {string} thesis - new thesis text (≤ MAX_THESIS_CHARS)
   * @returns {Promise<Object>} updated brief row
   */
  async updateSectionThesis(id, sectionKey, thesis) {
    if (!id) {
      createValidationError('briefId is required', { code: 'BRIEF_ID_REQUIRED' });
    }
    if (!sectionKey || typeof sectionKey !== 'string') {
      createValidationError('sectionKey is required', { code: 'SECTION_KEY_REQUIRED' });
    }
    const value = typeof thesis === 'string' ? thesis : '';
    if (value.length > MAX_THESIS_CHARS) {
      createValidationError(
        `thesis exceeds ${MAX_THESIS_CHARS} chars`,
        { code: 'THESIS_TOO_LONG' }
      );
    }

    const brief = await this.getBrief(id);
    if (!brief) {
      createValidationError(`Brief not found: ${id}`, { code: 'BRIEF_NOT_FOUND' });
    }

    const plans = { ...parseSectionPlans(brief.sectionPlans) };
    const existingSection = plans[sectionKey] && typeof plans[sectionKey] === 'object'
      ? plans[sectionKey]
      : {};
    plans[sectionKey] = { ...existingSection, thesis: value };

    const { data } = await this.client.models.EditorialBrief.update({
      id,
      sectionPlans: JSON.stringify(plans),
    });
    return data;
  }

  // ────────────────────────────────────────────────────────────────────
  // Consumption helpers (used by NewsletterAIService section integration)
  // ────────────────────────────────────────────────────────────────────

  /**
   * Returns the most recently-created APPROVED brief for the campaign's
   * series. Prefers campaign-scoped briefs; falls back to series default
   * (campaignId === null). Returns null when no approved brief exists.
   */
  async getActiveBriefForCampaign({ seriesId, campaignId = null } = {}) {
    if (!seriesId) return null;

    const all = await this.listBriefsBySeries(seriesId);
    const approved = all.filter((b) => b.briefStatus === 'approved');
    if (approved.length === 0) return null;

    // Prefer briefs scoped to this campaignId.
    const campaignScoped = approved.filter((b) => b.campaignId === campaignId);
    const seriesDefault = approved.filter((b) => b.campaignId === null || b.campaignId === undefined);

    const pickMostRecent = (list) =>
      list.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))[0];

    if (campaignScoped.length > 0) return pickMostRecent(campaignScoped);
    if (seriesDefault.length > 0) return pickMostRecent(seriesDefault);
    return pickMostRecent(approved);
  }

  /**
   * Pure helper. Reads brief.sectionPlans[sectionKey]?.thesis safely.
   * Returns null for any missing piece — never throws.
   *
   * #313: handles sectionPlans arriving as either a parsed object or a
   * JSON-encoded string (AppSync AWSJSON wire format). Older Amplify
   * clients return the string; newer ones return the parsed object.
   * Either way we resolve to the same shape.
   */
  getThesisForSection(brief, sectionKey) {
    if (!brief || !sectionKey) return null;
    const plans = parseSectionPlans(brief.sectionPlans);
    const section = plans[sectionKey];
    if (!section || typeof section !== 'object') return null;
    const thesis = section.thesis;
    if (typeof thesis !== 'string' || thesis.length === 0) return null;
    return thesis;
  }

  /**
   * Returns true when the VITE_USE_EDITORIAL_BRIEF flag is on. Cheap
   * shortcut for callers that want to skip the brief lookup entirely.
   */
  isEnabled() {
    return import.meta.env?.VITE_USE_EDITORIAL_BRIEF === 'true';
  }
}

const instance = new EditorialBriefService();
export default instance;
export { EditorialBriefService };
